import { prefersReducedMotion } from '../components/motion-policy.js';

function sectionIdFromLink(link) {
    const href = String(link.getAttribute('href') ?? '');
    if (!href.startsWith('#') || href.length === 1) {
        return '';
    }

    try {
        return decodeURIComponent(href.slice(1));
    } catch {
        return '';
    }
}

export function initPrivacyPage(
    documentRef = globalThis.document,
    windowRef = globalThis.window,
    ObserverRef = globalThis.IntersectionObserver,
) {
    const page = documentRef?.querySelector?.('[data-legal-document]');
    if (page === null || page === undefined) {
        return undefined;
    }

    const links = [...page.querySelectorAll('[data-legal-toc-link]')];
    const sections = [...page.querySelectorAll('[data-legal-section]')];
    const linksBySectionId = new Map(links.map((link) => [sectionIdFromLink(link), link]));
    const sectionOrder = new Map(sections.map((section, index) => [section, index]));

    if (links.length === 0 || sections.length === 0) {
        return undefined;
    }

    const setActiveSection = (section) => {
        const activeLink = linksBySectionId.get(String(section?.id ?? ''));
        if (activeLink === undefined) {
            return;
        }

        for (const link of links) {
            const isActive = link === activeLink;
            if (isActive) {
                link.setAttribute('aria-current', 'location');
            } else {
                link.removeAttribute('aria-current');
            }
        }
    };

    const clickHandlers = new Map();
    for (const link of links) {
        const section = sections.find((candidate) => candidate.id === sectionIdFromLink(link));
        if (section === undefined || typeof section.scrollIntoView !== 'function') {
            continue;
        }

        const handleClick = (event) => {
            event.preventDefault();
            setActiveSection(section);
            windowRef?.history?.pushState?.(null, '', `#${section.id}`);
            section.scrollIntoView({
                behavior: prefersReducedMotion(windowRef) ? 'auto' : 'smooth',
                block: 'start',
            });
        };

        clickHandlers.set(link, handleClick);
        link.addEventListener('click', handleClick);
    }

    const initialSection = sections.find((section) => `#${section.id}` === windowRef?.location?.hash)
        ?? sections[0];
    setActiveSection(initialSection);

    let observer;
    if (typeof ObserverRef === 'function') {
        const visibleSections = new Set();
        observer = new ObserverRef((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    visibleSections.add(entry.target);
                } else {
                    visibleSections.delete(entry.target);
                }
            }

            const orderedVisibleSections = [...visibleSections]
                .sort((first, second) => sectionOrder.get(first) - sectionOrder.get(second));
            const activeSection = orderedVisibleSections[orderedVisibleSections.length - 1];
            if (activeSection !== undefined) {
                setActiveSection(activeSection);
            }
        }, {
            rootMargin: '-28px 0px -60% 0px',
            threshold: 0,
        });

        for (const section of sections) {
            observer.observe(section);
        }
    }

    return {
        destroy() {
            observer?.disconnect();
            for (const [link, handler] of clickHandlers) {
                link.removeEventListener('click', handler);
            }
        },
    };
}

initPrivacyPage();
